import { useRef, useState, useEffect, useCallback } from "react";

const blogPosts = [
  {
    slug: "kredyt-w-sierpniu-2026-od-czego-zaczac",
    tag: "Sierpień 2026",
    title: "Kredyt w sierpniu 2026: od czego zacząć?",
    excerpt:
      "Prosty plan przygotowania do kredytu — bez presji, zgadywania i zbędnych formalności.",
    readTime: "6 min",
  },
  {
    slug: "stala-czy-zmienna-rata",
    tag: "Rata kredytu",
    title: "Stała czy zmienna rata? Jak wybrać spokojnie",
    excerpt:
      "Trzy pytania, które pomagają dopasować kredyt do Twojego budżetu, a nie odwrotnie.",
    readTime: "5 min",
  },
  {
    slug: "jak-zwiekszyc-zdolnosc-kredytowa",
    tag: "Zdolność kredytowa",
    title: "7 sposobów na wyższą zdolność kredytową",
    excerpt:
      "Proste ruchy, które potrafią podnieść zdolność o kilkadziesiąt tysięcy złotych. Bez magicznych sztuczek.",
    readTime: "4 min",
  },
];

export default function BlogSection() {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(1);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1024) setCardsPerView(3);
      else if (w >= 640) setCardsPerView(2);
      else setCardsPerView(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxSlide = Math.max(0, blogPosts.length - cardsPerView);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    const cardWidth = el.scrollWidth / blogPosts.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setCurrentSlide(Math.min(idx, maxSlide));
  }, [maxSlide]);

  const scrollToSlide = (idx: number) => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    const cardWidth = el.scrollWidth / blogPosts.length;
    el.scrollTo({
      left: idx * cardWidth,
      behavior: "smooth",
    });
  };

  const prevSlide = () => {
    const next = Math.max(currentSlide - 1, 0);
    scrollToSlide(next);
  };

  const nextSlide = () => {
    const next = Math.min(currentSlide + 1, maxSlide);
    scrollToSlide(next);
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
        <div className="text-center mb-10 md:mb-14">
          <div className="text-[11px] font-extrabold tracking-[3px] uppercase text-primary-600 mb-3">
            Blog · Poradnik kredytowy
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-900 mb-3">
            Kredyty prostymi słowami
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            Bez bankowego żargonu i drobnego druku. Praktyczna wiedza od
            doradcy z 25-letnim doświadczeniem.
          </p>
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-50 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg md:text-xl" />
            </button>

            <div
              ref={sliderRef}
              onScroll={handleScroll}
              className="flex-1 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{
                scrollSnapType: "x mandatory",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="flex gap-3 md:gap-4">
                {blogPosts.map((post, i) => (
                  <a
                    key={i}
                    href={`/blog/${post.slug}`}
                    className="flex-shrink-0 w-[260px] sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-12px)] snap-start text-left bg-white border border-background-200 rounded-xl p-6 hover:shadow-md hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-extrabold tracking-widest text-primary-600 uppercase">
                        {post.tag}
                      </span>
                      <span className="text-[10px] font-semibold text-foreground-400">
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-sm md:text-base font-extrabold tracking-tight leading-snug mb-3">
                      {post.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="text-xs font-bold text-primary-600 hover:text-primary-500 transition-colors">
                      Czytaj artykuł &rarr;
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide >= maxSlide}
              className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-50 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg md:text-xl" />
            </button>
          </div>

          <div className="flex justify-center gap-1.5 mt-5">
            {Array.from({ length: maxSlide + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToSlide(i)}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  i === currentSlide
                    ? "bg-primary-500 w-5"
                    : "bg-background-300 hover:bg-background-400 w-2"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <a
            href="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Zobacz wszystkie artykuły
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
